import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Zap, Check, User, CalendarCheck, Swords, Salad, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import ConnectionMotif from "@/components/ConnectionMotif";
import FiberUnderline from "@/components/FiberUnderline";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#0f0f13] px-4 py-3 text-sm text-white placeholder-white/30 outline-none transition-colors focus:border-[#f5c542] focus:ring-1 focus:ring-[#f5c542]";
const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-white/50";

const Field = ({ label, children, required }) => (
  <div>
    <label className={labelCls}>
      {label} {required && <span className="text-[#e60000]">*</span>}
    </label>
    {children}
  </div>
);

function Choice({ options, value, onChange, testid }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid={testid}>
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            data-testid={`${testid}-${o.value}`}
            className={`rounded-xl border px-4 py-3 text-sm font-medium transition-all ${
              active
                ? "border-[#f5c542] bg-[#f5c542]/10 text-white shadow-[0_0_18px_rgba(245, 197, 66,0.3)]"
                : "border-white/10 bg-[#0f0f13] text-white/60 hover:border-white/30"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

const Toggle = ({ checked, onChange, label, testid }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    data-testid={testid}
    className={`flex w-full items-start gap-3 rounded-xl border p-4 text-left transition-all ${
      checked ? "border-[#e60000] bg-[#e60000]/10" : "border-white/10 bg-[#0f0f13] hover:border-white/30"
    }`}
  >
    <span
      className={`mt-0.5 grid h-5 w-5 flex-shrink-0 place-items-center rounded border ${
        checked ? "border-[#e60000] bg-[#e60000]" : "border-white/30"
      }`}
    >
      {checked && <Check size={13} className="text-black" />}
    </span>
    <span className="text-sm text-white/80">{label}</span>
  </button>
);

const LEVELS = [
  { value: "base", label: "Base" },
  { value: "avanzato", label: "Avanzato" },
];

const initial = {
  first_name: "", last_name: "", email: "", phone: "", company: "", role: "", city: "",
  attending: "", companions: 0,
  tournament: "", padel_level: "", beach_level: "", open_to_team: false, team_members: "",
  diet: "nessuna", diet_notes: "", tshirt: "",
  consent_privacy: false, consent_media: false, consent_comms: false,
};

export default function RegistrationForm({ settings, stats, preselect, onSuccess }) {
  const [form, setForm] = useState(initial);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (preselect) {
      setForm((f) => ({ ...f, attending: "yes", tournament: preselect }));
    }
  }, [preselect]);

  const steps = useMemo(() => {
    const base = [
      { key: "dati", label: "I tuoi dati", icon: User },
      { key: "partecipazione", label: "La tua partecipazione", icon: CalendarCheck },
    ];
    if (form.attending === "no") {
      return [...base, { key: "conferma", label: "Conferma", icon: ShieldCheck }];
    }
    return [
      ...base,
      { key: "sfida", label: "Scegli la sfida", icon: Swords },
      { key: "preferenze", label: "Preferenze", icon: Salad },
      { key: "conferma", label: "Conferma", icon: ShieldCheck },
    ];
  }, [form.attending]);

  const current = steps[Math.min(step, steps.length - 1)];
  const progress = ((step + 1) / steps.length) * 100;

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);

  const validateStep = () => {
    const k = current.key;
    if (k === "dati") {
      if (!form.first_name || !form.last_name || !form.email || !form.phone)
        return "Compila tutti i campi obbligatori.";
      if (!emailValid) return "Inserisci un'email valida.";
    }
    if (k === "partecipazione") {
      if (!form.attending) return "Indica se parteciperai.";
    }
    if (k === "sfida") {
      if (!form.tournament) return "Seleziona un'opzione per i tornei.";
      if (form.tournament === "padel" && !form.padel_level)
        return "Indica il tuo livello di padel.";
    }
    if (k === "conferma") {
      if (!form.consent_privacy) return "Devi accettare l'informativa sulla privacy.";
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) return toast.error(err);
    if (step < steps.length - 1) setStep(step + 1);
  };
  const back = () => setStep(Math.max(0, step - 1));

  const submit = async () => {
    const err = validateStep();
    if (err) return toast.error(err);
    setSubmitting(true);
    try {
      const payload = { ...form, companions: Number(form.companions) || 0 };
      if (payload.attending === "no") {
        payload.tournament = "nessuno";
      }
      const { data } = await api.post("/registrations", payload);
      onSuccess(data.registration);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Invio non riuscito.");
    } finally {
      setSubmitting(false);
    }
  };

  const showPadel = form.tournament === "padel";
  const showBeach = form.tournament === "beach";

  const summaryRows = [
    ["Nome", `${form.first_name} ${form.last_name}`],
    ["Email", form.email],
    ["Telefono", form.phone],
    ["Partecipazione", form.attending === "yes" ? "Sì, parteciperò" : "No, non potrò partecipare"],
  ];
  if (form.attending === "yes") {
    const tMap = { padel: "Torneo di Padel", beach: "Torneo di Beach Volley", nessuno: "Nessun torneo" };
    summaryRows.push(["Attività", tMap[form.tournament] || "-"]);
    if (form.tournament === "padel" && form.padel_level)
      summaryRows.push(["Livello Padel", form.padel_level]);
    summaryRows.push(["Preferenze alimentari", form.diet]);
  }

  return (
    <section id="registrazione" className="relative isolate mx-auto w-full max-w-3xl overflow-hidden px-6 py-24 md:py-32" data-testid="registration-section">
      <ConnectionMotif color="#f5c542" color2="#e60000" className="-z-10 opacity-[0.1]" />
      <div className="speed-line mb-16" />
      <p className="text-center text-xs font-semibold uppercase tracking-[0.3em] text-[#e60000]">Conferma</p>
      <h2 className="mt-4 text-center font-display text-3xl font-black tracking-tight sm:text-4xl lg:text-5xl">
        CONFERMA LA TUA PRESENZA
      </h2>
      <FiberUnderline center />
      <p className="mt-4 text-center text-white/60">Compila il modulo e preparati a vivere la convention.</p>

      {/* Progress */}
      <div className="mt-12">
        <div className="mb-3 flex items-center justify-between">
          {steps.map((s, i) => (
            <div key={s.key} className="flex flex-1 flex-col items-center gap-1.5">
              <div
                className={`grid h-9 w-9 place-items-center rounded-full border text-xs transition-all ${
                  i <= step
                    ? "border-[#f5c542] bg-[#f5c542]/15 text-[#f5c542] shadow-[0_0_14px_rgba(245, 197, 66,0.4)]"
                    : "border-white/15 text-white/40"
                }`}
              >
                {i < step ? <Check size={15} /> : <s.icon size={15} />}
              </div>
              <span className="hidden text-[10px] uppercase tracking-wide text-white/40 sm:block">{s.label}</span>
            </div>
          ))}
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-[#f5c542] via-[#ff7a1a] to-[#e60000]"
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
      </div>

      {/* Steps */}
      <div className="mt-10 rounded-2xl border border-white/10 bg-[#0f0f13]/70 p-6 backdrop-blur sm:p-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={current.key}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.3 }}
            className="space-y-5"
          >
            {current.key === "dati" && (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Nome" required>
                    <input className={inputCls} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="field-first-name" />
                  </Field>
                  <Field label="Cognome" required>
                    <input className={inputCls} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="field-last-name" />
                  </Field>
                </div>
                <Field label="Email aziendale" required>
                  <input type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="field-email" />
                </Field>
                <Field label="Numero di telefono" required>
                  <input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="field-phone" />
                </Field>
              </>
            )}

            {current.key === "partecipazione" && (
              <>
                <Field label="Confermi la partecipazione?" required>
                  <Choice
                    testid="field-attending"
                    value={form.attending}
                    onChange={(v) => set("attending", v)}
                    options={[
                      { value: "yes", label: "Sì, parteciperò" },
                      { value: "no", label: "No, non potrò partecipare" },
                    ]}
                  />
                </Field>
                {form.attending === "no" && (
                  <p className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/60">
                    Ci dispiace! Prosegui per confermare la tua risposta.
                  </p>
                )}
              </>
            )}

            {current.key === "sfida" && (
              <>
                <Field label="Partecipazione alle attività sportive" required>
                  <Choice
                    testid="field-tournament"
                    value={form.tournament}
                    onChange={(v) => set("tournament", v)}
                    options={[
                      { value: "padel", label: "Torneo di Padel" },
                      { value: "beach", label: "Beach Volley" },
                      { value: "nessuno", label: "Nessun torneo" },
                    ]}
                  />
                </Field>
                {stats?.padel_full && form.tournament === "padel" && (
                  <p className="text-xs text-[#ff6b6b]">Attenzione: posti Padel esauriti.</p>
                )}
                {showPadel && (
                  <Field label="Livello di gioco — Padel" required>
                    <Choice testid="field-padel-level" value={form.padel_level} onChange={(v) => set("padel_level", v)} options={LEVELS} />
                  </Field>
                )}
              </>
            )}

            {current.key === "preferenze" && (
              <>
                <Field label="Preferenze alimentari" required>
                  <Choice
                    testid="field-diet"
                    value={form.diet}
                    onChange={(v) => set("diet", v)}
                    options={[
                      { value: "nessuna", label: "Nessuna" },
                      { value: "vegetariana", label: "Vegetariana" },
                      { value: "vegana", label: "Vegana" },
                      { value: "senza_glutine", label: "Senza glutine" },
                      { value: "allergie", label: "Intolleranze/Allergie" },
                    ]}
                  />
                </Field>
                {form.diet === "allergie" && (
                  <Field label="Descrivi intolleranze o allergie">
                    <textarea rows={3} className={inputCls} value={form.diet_notes} onChange={(e) => set("diet_notes", e.target.value)} data-testid="field-diet-notes" />
                  </Field>
                )}
              </>
            )}

            {current.key === "conferma" && (
              <>
                <div className="rounded-xl border border-white/10 bg-white/5 p-5" data-testid="summary-box">
                  <h4 className="mb-3 font-display text-sm font-bold uppercase tracking-wider text-[#f5c542]">Riepilogo</h4>
                  <dl className="space-y-2">
                    {summaryRows.map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-4 text-sm">
                        <dt className="text-white/40">{k}</dt>
                        <dd className="text-right font-medium text-white">{v}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
                <div className="space-y-3">
                  <Toggle testid="consent-privacy" checked={form.consent_privacy} onChange={(v) => set("consent_privacy", v)} label="Accetto l'informativa privacy e il trattamento dei dati personali. *" />
                  <Toggle testid="consent-media" checked={form.consent_media} onChange={(v) => set("consent_media", v)} label="Acconsento a fotografie e riprese video durante l'evento." />
                  <Toggle testid="consent-comms" checked={form.consent_comms} onChange={(v) => set("consent_comms", v)} label="Desidero ricevere comunicazioni organizzative." />
                </div>
              </>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Nav */}
        <div className="mt-8 flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={back}
            disabled={step === 0}
            className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-white/5 disabled:opacity-30"
            data-testid="form-back-btn"
          >
            <ArrowLeft size={16} /> Indietro
          </button>

          {step < steps.length - 1 ? (
            <button
              type="button"
              onClick={next}
              className="btn-neon flex items-center gap-2 rounded-full px-7 py-3 font-display text-sm font-extrabold uppercase tracking-[0.12em]"
              data-testid="form-next-btn"
            >
              Avanti <ArrowRight size={16} />
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              className="btn-neon animate-pulse-glow flex items-center gap-2 rounded-full px-8 py-3 font-display text-sm font-extrabold uppercase tracking-[0.12em] disabled:opacity-60"
              data-testid="form-submit-btn"
            >
              <Zap size={18} /> {submitting ? "Invio..." : "Ci sarò!"}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
